import React, { ReactNode } from 'react';
import { get } from 'lodash';

import {
  Box,
  Container,
  ContainerProps,
  IconButton,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { links } from '../../paths';

import { Menu } from '../../atoms/icons/menu';
import { Settings } from '../../atoms/icons/settings';
import { Logo } from '../../atoms/logo';
import { Boiler } from '../../atoms/icons/boiler';
import { ElectricalGrid } from '../../atoms/icons/electrical-grid';
import { SolarPanel } from '../../atoms/icons/solar-panel';
import { Statistics } from '../../atoms/icons/statistics';
import { HeatPump } from '../../atoms/icons/heat-pump';
import { Wallbox } from '../../atoms/icons/wallbox';
import { Wind } from '../../atoms/icons/wind';
import { Thermometer } from '../../atoms/icons/thermometer';
import { Notification } from '../../atoms/icons/notification';
import { Plus } from '../../atoms/icons/plus';
import { Reports } from '../../atoms/icons/reports';

import { useSelector } from '../../store/utils';

import { LinkComponent } from '../../atoms/link-component';

export type HeaderProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  icon?:
    | 'boiler'
    | 'electrical-grid'
    | 'solar-panel'
    | 'statistics'
    | 'settings'
    | 'heat-pump'
    | 'spot'
    | 'plug'
    | 'wallbox'
    | 'wind'
    | 'thermostat'
    | 'reports';
  desktopNavToggle: () => void;
} & Omit<ContainerProps, 'title'>;

export const Header = ({
  title,
  subtitle,
  icon,
  desktopNavToggle,
  ...props
}: HeaderProps) => {
  const premium = useSelector(({ variables }) => get(variables, 'PREMIUM'));

  const md = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  return (
    <Container
      {...props}
      component="header"
      sx={{ display: 'flex', flexDirection: 'row', mb: 4, ...(props.sx || {}) }}
    >
      {/* icon */}
      <Box
        sx={{
          borderRadius: '50%',
          bgcolor: 'primary.main',
          width: 48,
          height: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'common.white',
          mr: 3,
          position: 'relative',
        }}
      >
        {!icon && <Logo variant="header" sx={{ fontSize: 22, mt: -0.25 }} />}
        {icon === 'boiler' && <Boiler sx={{ fontSize: 40 }} />}
        {icon === 'electrical-grid' && <ElectricalGrid sx={{ fontSize: 40 }} />}
        {icon === 'solar-panel' && <SolarPanel sx={{ fontSize: 40 }} />}
        {icon === 'statistics' && <Statistics sx={{ fontSize: 20 }} />}
        {icon === 'settings' && <Settings sx={{ fontSize: 20 }} />}
        {icon === 'heat-pump' && <HeatPump sx={{ fontSize: 26 }} />}
        {icon === 'wallbox' && (
          <Wallbox sx={{ fontSize: 28, ml: 0.35, mt: 0.25 }} />
        )}
        {icon === 'wind' && <Wind sx={{ fontSize: 26 }} />}
        {icon === 'thermostat' && <Thermometer sx={{ fontSize: 26 }} />}
        {icon === 'reports' && <Reports sx={{ fontSize: 28, ml: '2px' }} />}

        {premium && <Plus sx={{ position: 'absolute', top: 0, right: 0 }} />}
      </Box>

      {/* title */}
      <Stack sx={{ mt: 0.25 }}>
        <Typography variant="h1">{title}</Typography>
        {subtitle && (
          <Typography variant="h5" color="text.disabled">
            {subtitle}
          </Typography>
        )}
      </Stack>

      {/* settings */}
      <Box
        sx={{
          ml: 'auto',
          '& .MuiSvgIcon-root': { fontSize: 20 },
        }}
      >
        {premium && (
          <IconButton to={links.appMessages} component={LinkComponent}>
            <Notification />
          </IconButton>
        )}

        {md ? (
          <IconButton onClick={desktopNavToggle} className="desktop-nav-open">
            <Menu />
          </IconButton>
        ) : (
          <IconButton to={links.settings || '#'} component={LinkComponent}>
            <Settings />
          </IconButton>
        )}
      </Box>
    </Container>
  );
};
